/**
 * OnboardingBanner (Doc 49) — gợi ý setup khi user chưa hoàn chỉnh.
 *
 * Variants:
 * - missing-llm-key (admin only): "Chưa configure LLM API key. Setup..."
 * - missing-odoo: "Bạn chưa link Odoo identity — chỉ AI conversation cơ bản."
 * - all-good: không render.
 *
 * Dismiss persist localStorage key per-variant.
 */
import { AlertCircle, ArrowRight, Cpu, Key, Plug, X } from "lucide-react";
import * as React from "react";

import { onboardingApi, type OnboardingStatus } from "@/api/onboarding";
import { useAuthStore } from "@/stores/auth";
import { useRouteStore } from "@/stores/route";

const DISMISS_KEY_PREFIX = "onboarding_banner_dismissed:";

function isDismissed(variant: string): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + variant) === "1";
  } catch {
    return false;
  }
}

function dismiss(variant: string): void {
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + variant, "1");
  } catch {
    // ignore
  }
}

export function OnboardingBanner(): JSX.Element | null {
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const navigate = useRouteStore((s) => s.navigate);
  const [status, setStatus] = React.useState<OnboardingStatus | null>(null);
  const [, force] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    if (!isAuth) return;
    void (async () => {
      try {
        const s = await onboardingApi.status();
        setStatus(s);
      } catch {
        // ignore — banner is optional
      }
    })();
  }, [isAuth]);

  if (!status || status.missing_setup.length === 0) return null;

  // Priority: llm_key trước (block toàn bộ chat) → odoo_identity sau
  const variantsToShow = status.missing_setup.filter(
    (m) => !isDismissed(m),
  );
  if (variantsToShow.length === 0) return null;

  const variant = variantsToShow.includes("llm_key")
    ? "llm_key"
    : variantsToShow[0]!; // length > 0 guaranteed by guard above

  const handleDismiss = (): void => {
    dismiss(variant);
    force();
  };

  if (variant === "llm_key") {
    return (
      <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 mx-4 my-2 rounded">
        <div className="flex items-start gap-3">
          <Key className="size-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">
              Chưa configure LLM API key
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Để chat hoạt động, cần ít nhất 1 API key (Anthropic / OpenAI /
              Google / DeepSeek) HOẶC bật Local LLM (Ollama đã sẵn sàng).
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => navigate("settings.secrets")}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline"
              >
                <ArrowRight className="size-3" /> Set API key
              </button>
              <button
                onClick={() => navigate("admin.localLlm")}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline"
              >
                <Cpu className="size-3" /> Bật Ollama on-prem
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Đóng"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === "odoo_identity") {
    return (
      <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 mx-4 my-2 rounded">
        <div className="flex items-start gap-3">
          <Plug className="size-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">
              Tài khoản chưa link Odoo identity
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Chat AI cơ bản hoạt động bình thường. Tools ERP (search_partner,
              create_quotation…) cần Odoo mapping —{" "}
              {status.is_admin
                ? "vào Admin → Users để map."
                : "liên hệ admin để map."}
            </p>
            {status.is_admin ? (
              <button
                onClick={() => navigate("admin.users")}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
              >
                <ArrowRight className="size-3" /> Mở Admin Users
              </button>
            ) : null}
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Đóng"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    );
  }

  // Fallback unknown variant
  return (
    <div className="border-l-4 border-yellow-500 bg-yellow-50 px-4 py-3 mx-4 my-2 rounded">
      <div className="flex items-center gap-3">
        <AlertCircle className="size-5 text-yellow-600 shrink-0" />
        <p className="flex-1 text-sm">
          Cần setup thêm: <code className="text-xs">{variant}</code>
        </p>
        <button onClick={handleDismiss} aria-label="Đóng">
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
